import { CONFIG } from '../../Config/config'
import store from '../../Config/store'
import { authorization } from '../Auth/Auth'
import * as ERRORS from '../../Reducers/Errors/const'
import { ROLE_ADMIN } from '../../shared/constants'
import { isProduction } from '../../Utils/Functions'

class APIXero {
  path = {
    baseUrl: `${CONFIG.API_BASE_URL.replace('/platform', '')}`,
  }

  isHaveXeroAccess = () => {
    const user = authorization.getUser()
    return user.role === ROLE_ADMIN && isProduction()
  }

  checkIsValidXeroToken = async () => {
    if (!this.isHaveXeroAccess()) {
      return
    }
    const { xeroAccessToken, xeroRefreshToken } = authorization.getXeroTokens()

    const request = await this.callEndpoint({
      url: '/xero-refresh-token',
      body: {
        xeroAccessToken,
        xeroRefreshToken,
      },
    })

    const result = await request.json()

    if (result.isNew) {
      authorization.setXeroTokens({
        xeroAccessToken: result.xeroAccessToken,
        xeroRefreshToken: result.xeroRefreshToken,
      })
    }
  }

  getXeroTenant = async ({
    includeAvailableAccount = false,
    includeCurrencies = false,
  } = {}) => {
    if (!this.isHaveXeroAccess()) {
      return
    }
    await this.checkIsValidXeroToken()

    const tenantId = authorization.getXeroTenantId()

    const { xeroAccessToken } = authorization.getXeroTokens()

    if (!tenantId) {
      return null
    }

    const request = await this.callEndpoint({
      url: '/get-xero-tenant',
      body: {
        tenantId,
        xeroAccessToken,
        includeAvailableAccount,
        includeCurrencies,
      },
    })
    return await request.json()
  }

  getXeroContact = async ({ tenantId, contactName, contactId } = {}) => {
    if (!this.isHaveXeroAccess()) {
      return
    }
    const { xeroAccessToken } = authorization.getXeroTokens()

    if (!tenantId) {
      return null
    }

    const request = await this.callEndpoint({
      url: '/get-xero-contact',
      body: {
        xeroAccessToken,
        contactName,
        contactId,
        tenantId,
      },
    })
    return await request.json()
  }

  getXeroContacts = async ({ tenantId } = {}) => {
    if (!this.isHaveXeroAccess()) {
      return
    }
    await this.checkIsValidXeroToken()

    const { xeroAccessToken } = authorization.getXeroTokens()

    if (!tenantId) {
      return null
    }

    const request = await this.callEndpoint({
      url: '/get-xero-contacts',
      body: {
        xeroAccessToken,
        tenantId,
      },
    })
    return await request.json()
  }

  getXeroTrackingCategories = async ({ tenantId } = {}) => {
    if (!this.isHaveXeroAccess()) {
      return
    }
    await this.checkIsValidXeroToken()

    const { xeroAccessToken } = authorization.getXeroTokens()

    if (!tenantId) {
      return null
    }

    const request = await this.callEndpoint({
      url: '/get-xero-tracking-categories',
      body: {
        xeroAccessToken,
        tenantId,
      },
    })
    return await request.json()
  }

  getXeroItemCodes = async ({ tenantId } = {}) => {
    if (!this.isHaveXeroAccess()) {
      return
    }

    await this.checkIsValidXeroToken()

    const { xeroAccessToken } = authorization.getXeroTokens()

    if (!tenantId) {
      return null
    }

    const request = await this.callEndpoint({
      url: '/get-xero-item-codes',
      body: {
        xeroAccessToken,
        tenantId,
      },
    })
    return await request.json()
  }

  getXeroInvoices = async ({ tenantId, contactID } = {}) => {
    if (!this.isHaveXeroAccess()) {
      return
    }

    await this.checkIsValidXeroToken()

    const { xeroAccessToken } = authorization.getXeroTokens()

    if (!tenantId) {
      return null
    }

    const request = await this.callEndpoint({
      url: '/get-xero-invoices',
      body: {
        xeroAccessToken,
        tenantId,
        contactID,
      },
    })
    return await request.json()
  }

  getXeroItem = async ({ tenantId, campaignId, contactID } = {}) => {
    if (!this.isHaveXeroAccess()) {
      return
    }
    const { xeroAccessToken } = authorization.getXeroTokens()

    if (!tenantId) {
      return null
    }

    const request = await this.callEndpoint({
      url: '/get-xero-item',
      body: {
        xeroAccessToken,
        campaignId,
        tenantId,
        contactID,
      },
    })
    return await request.json()
  }

  getXeroConnectedTenants = async () => {
    if (!this.isHaveXeroAccess()) {
      return
    }
    await this.checkIsValidXeroToken()

    const { xeroAccessToken } = authorization.getXeroTokens()
    const request = await this.callEndpoint({
      url: '/get-xero-connected-tenants',
      body: { xeroAccessToken },
    })
    return await request.json()
  }

  createXeroContact = async ({ contact, tenantId, clientName }) => {
    if (!this.isHaveXeroAccess()) {
      return
    }
    await this.checkIsValidXeroToken()

    const { xeroAccessToken } = authorization.getXeroTokens()
    const request = await this.callEndpoint({
      url: '/create-xero-contact',
      body: {
        xeroAccessToken,
        contact,
        tenantId,
        clientName,
      },
    })
    return await request.json()
  }

  updateXeroContact = async ({
    tenantId,
    contact,
    oldName,
    isNeedToUpdateTracking,
    clientName,
  }) => {
    if (!this.isHaveXeroAccess()) {
      return
    }
    await this.checkIsValidXeroToken()

    const { xeroAccessToken } = authorization.getXeroTokens()
    const request = await this.callEndpoint({
      url: '/update-xero-contact',
      body: {
        xeroAccessToken,
        contact,
        tenantId,
        oldName,
        isNeedToUpdateTracking,
        clientName,
      },
    })
    return await request.json()
  }

  disconnectXero = async id => {
    if (!this.isHaveXeroAccess()) {
      return
    }
    await this.checkIsValidXeroToken()

    const { xeroAccessToken } = authorization.getXeroTokens()
    const request = await this.callEndpoint({
      url: '/disconnect-xero',
      body: {
        xeroAccessToken,
        id,
      },
    })
    return await request.json()
  }

  createOrUpdateXeroItem = async ({ item, tenantId }) => {
    if (!this.isHaveXeroAccess()) {
      return
    }
    await this.checkIsValidXeroToken()

    const { xeroAccessToken } = authorization.getXeroTokens()

    const request = await this.callEndpoint({
      url: '/create-or-update-xero-item',
      body: {
        xeroAccessToken,
        item,
        tenantId,
      },
    })
    return await request.json()
  }

  createXeroInvoice = async ({
    itemCode,
    campaignId,
    tenantId,
    contactID,
    clientName,
  }) => {
    if (!this.isHaveXeroAccess()) {
      return
    }
    await this.checkIsValidXeroToken()

    const { xeroAccessToken } = authorization.getXeroTokens()

    const request = await this.callEndpoint({
      url: '/create-xero-invoice',
      body: {
        xeroAccessToken,
        itemCode,
        campaignId,
        tenantId,
        contactID,
        clientName,
      },
    })
    return await request.json()
  }

  updateXeroInvoice = async ({
    itemCode,
    campaignId,
    tenantId,
    invoiceID,
    contactID,
    clientName,
  }) => {
    if (!this.isHaveXeroAccess()) {
      return
    }
    await this.checkIsValidXeroToken()

    const { xeroAccessToken } = authorization.getXeroTokens()

    const request = await this.callEndpoint({
      url: '/update-xero-invoice',
      body: {
        xeroAccessToken,
        itemCode,
        campaignId,
        tenantId,
        invoiceID,
        contactID,
        clientName,
      },
    })
    return await request.json()
  }

  creatingContactAndItemAndTrackingAndInvoice = async ({
    campaignId,
    tenantId,
  }) => {
    if (!this.isHaveXeroAccess()) {
      return
    }
    await this.checkIsValidXeroToken()

    const { xeroAccessToken } = authorization.getXeroTokens()

    const request = await this.callEndpoint({
      url: '/creating-contact-tracking-item-invoice',
      body: {
        xeroAccessToken,
        campaignId,
        tenantId,
      },
    })
    return await request.json()
  }

  checkOnSimilarContact = async ({ contactName, tenantId }) => {
    if (!this.isHaveXeroAccess()) {
      return
    }

    const { xeroAccessToken } = authorization.getXeroTokens()

    const request = await this.callEndpoint({
      url: '/check-on-similar-contact',
      body: {
        xeroAccessToken,
        contactName,
        tenantId,
      },
    })
    return await request.json()
  }

  addPaymentsToAuthorisedInvoices = async ({ tenantId, invoices }) => {
    if (!this.isHaveXeroAccess()) {
      return
    }
    await this.checkIsValidXeroToken()

    const { xeroAccessToken } = authorization.getXeroTokens()

    const request = await this.callEndpoint({
      url: '/add-payments-to-authorised-invoices',
      body: {
        xeroAccessToken,
        tenantId,
        invoices,
      },
    })
    return await request.json()
  }

  getAuthorisedInvoices = async ({ tenantId }) => {
    if (!this.isHaveXeroAccess()) {
      return
    }
    await this.checkIsValidXeroToken()

    const { xeroAccessToken } = authorization.getXeroTokens()

    const request = await this.callEndpoint({
      url: '/get-authorised-invoices',
      body: {
        xeroAccessToken,
        tenantId,
      },
    })
    return await request.json()
  }

  callEndpoint = async ({ body, url, options = {} }) => {
    const headers = new Headers({
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      /* TODO: simple temporary solution - should be verified before request */
      'x-api-key': authorization.getAPIKey(),
    })

    return await new Promise((resolve, reject) => {
      fetch(`${this.path.baseUrl}${url}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      })
        .then(async response => {
          const result = await response.clone()
          const jsonResult = await result.json()
          if (jsonResult.error && !options.ignoreErrors) {
            const error = jsonResult.error
            if (!options.doNotShowErrorModal) {
              store.dispatch({
                type: ERRORS.PUSH_API_ERROR,
                error,
              })
            }
            reject(error)
          } else {
            resolve(response)
          }
        })
        .catch(error => {
          if (!options.ignoreErrors) {
            resolve(error)
          }
        })
    })
  }
}

export const apiXero = new APIXero()
